import { useEffect, useMemo, useRef, useState } from "react"
import { debounce } from "ts-debounce"
import backend from "~/backend"
import useDidUpdateEffect from "~/hooks/useDidUpdateEffect"

type Props = {
  dayId: number
  initialData: DietType
}

type CalorieDay = { date: string; calories: number | null; weight: number | null }

type LoggedDay = { i: number; v: number; date: string; weight: number | null }

type DietType = {
  weight: string
  calories: string
  target: number | null
  history: CalorieDay[]
}

const SAGE = "#7BA589"
const OVER = "#B0405E"
const TARGET_LINE = "#4A6B8A"
const AVG_LINE = "#423E36"
const BASELINE = "#D8CEBA"
const DOW_LABEL = "#9C958A"
const DOW = ["S", "M", "T", "W", "T", "F", "S"]

function roundedTop(x: number, y: number, w: number, h: number) {
  const r = Math.min(2.5, w / 2, h)
  const bottom = y + h
  return `M${x},${bottom} L${x},${y + r} Q${x},${y} ${x + r},${y} L${x + w - r},${y} Q${x + w},${y} ${x + w},${y + r} L${x + w},${bottom} Z`
}

function weekday(iso: string) {
  return DOW[new Date(`${iso}T00:00:00`).getDay()]
}

function formatDate(iso: string) {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function CaloriesChart({ target, history }: { target: number | null; history: CalorieDay[] }) {
  const geom = useMemo(() => {
    if (!target || !history?.length) return null

    const n = history.length
    const logged = history
      .map((d, i) => (d.calories != null ? { i, v: d.calories, date: d.date, weight: d.weight } : null))
      .filter((p): p is LoggedDay => p !== null)

    if (logged.length < 2) return null

    const forAvg = logged.filter((d) => d.i !== n - 1)
    const rolled = forAvg.map((p, k) => {
      const win = forAvg.slice(Math.max(0, k - 6), k + 1)
      return { i: p.i, avg: win.reduce((a, b) => a + b.v, 0) / win.length }
    })

    const topVal = Math.max(...logged.map((p) => p.v), target) * 1.1

    const W = 324
    const top = 12
    const bottom = 94
    const padX = 8
    const y = (v: number) => top + ((topVal - v) / topVal) * (bottom - top)
    const pitch = (W - padX * 2) / n
    const cx = (i: number) => padX + pitch * (i + 0.5)
    const bodyW = Math.min(15, pitch * 0.55)

    const current = rolled.length ? rolled[rolled.length - 1].avg : null

    return {
      n, logged, rolled, y, cx, pitch, bodyW,
      ceilingY: y(target), baselineY: bottom, top, padX, W,
      current: current != null ? Math.round(current) : null,
      delta: current != null ? Math.round(target - current) : null
    }
  }, [target, history])

  const [hover, setHover] = useState<LoggedDay | null>(null)

  if (!geom) return null

  const { n, logged, rolled, y, cx, pitch, bodyW, ceilingY, baselineY, top, padX, W, current, delta } = geom
  const under = delta != null && delta >= 0
  const last = rolled.length ? rolled[rolled.length - 1] : null

  return (
    <div className="mt-1 flex flex-col gap-2 border-t border-border pt-3">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-medium uppercase text-fg-muted">Last 3 weeks</span>
        {current != null && delta != null && (
          <span className={`text-sm font-semibold tabular-nums ${under ? "text-success" : "text-danger"}`}>
            {current.toLocaleString()} <span className="text-xs font-medium">avg</span>
            <span
              className={`ml-1 rounded-full px-2 py-0.5 text-xs font-bold ${
                under ? "bg-success-bg text-success" : "bg-danger-bg text-danger"
              }`}
            >
              {Math.abs(delta)} {under ? "under" : "over"}
            </span>
          </span>
        )}
      </div>

      <div className="relative" onMouseLeave={() => setHover(null)}>
        <svg viewBox="0 0 324 110" className="w-full" role="img" aria-label={`${n}-day calories against a ${target} kcal target`}>
          {[7, 14]
            .filter((w) => w < n)
            .map((w) => {
              const x = padX + pitch * w
              return <line key={`sep-${w}`} x1={x} x2={x} y1={top - 2} y2={baselineY} stroke="#E7DECB" strokeWidth={1} />
            })}

          {logged.map(({ i, v }) => {
            const x = cx(i) - bodyW / 2
            const topY = y(v)
            if (v > target!) {
              const redTop = Math.min(topY, ceilingY - 2)
              return (
                <g key={`candle-${i}`}>
                  <rect x={x} y={ceilingY} width={bodyW} height={Math.max(1, baselineY - ceilingY)} fill={SAGE} />
                  <path d={roundedTop(x, redTop, bodyW, ceilingY - redTop)} fill={OVER} />
                </g>
              )
            }
            return <path key={`candle-${i}`} d={roundedTop(x, topY, bodyW, baselineY - topY)} fill={SAGE} />
          })}

          <line x1={padX} x2={W - padX} y1={baselineY} y2={baselineY} stroke={BASELINE} strokeWidth={1} />

          <line
            x1={padX}
            x2={W - padX}
            y1={ceilingY}
            y2={ceilingY}
            stroke={TARGET_LINE}
            strokeWidth={1.5}
            strokeDasharray="4 3"
            opacity={0.45}
          />

          {rolled.length > 1 && (
            <polyline
              points={rolled.map((p) => `${cx(p.i)},${y(p.avg)}`).join(" ")}
              fill="none"
              stroke={AVG_LINE}
              strokeWidth={1.5}
              strokeDasharray="1 3"
              strokeLinecap="round"
              opacity={0.7}
            />
          )}
          {last && <circle cx={cx(last.i)} cy={y(last.avg)} r={2.3} fill={AVG_LINE} />}

          {history.map((d, i) => (
            <text key={`dow-${i}`} x={cx(i)} y={105} textAnchor="middle" fontSize={7.5} fontWeight={600} fill={DOW_LABEL}>
              {weekday(d.date)}
            </text>
          ))}

          {logged.map((d) => (
            <rect
              key={`hit-${d.i}`}
              x={cx(d.i) - pitch / 2}
              y={top - 2}
              width={pitch}
              height={baselineY - top + 2}
              fill="transparent"
              style={{ pointerEvents: "all" }}
              onMouseEnter={() => setHover(d)}
            />
          ))}
        </svg>

        {hover && (
          <div
            className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded bg-fg px-2 py-1 text-center text-xs leading-tight text-white shadow-md"
            style={{ left: `${(cx(hover.i) / W) * 100}%`, top: `calc(${(y(hover.v) / 110) * 100}% - 6px)` }}
          >
            <div className="font-semibold">{formatDate(hover.date)}</div>
            <div className="tabular-nums">{hover.v.toLocaleString()} kcal</div>
            {hover.weight != null && <div className="tabular-nums">{hover.weight} kg</div>}
          </div>
        )}
      </div>
    </div>
  )
}

export default function Diet({ dayId, initialData }: Props) {
  const [weight, setWeight] = useState(initialData.weight)
  const [calories, setCalories] = useState(initialData.calories)
  const [history, setHistory] = useState(initialData.history)

  const saved = useRef(initialData)

  const save = (key, value) => {
    if (saved.current[key] === value) return

    saved.current[key] = value
    backend.updateDiet(dayId, { [key]: value })
  }

  const persistWeight = useMemo(
    () => debounce((weight) => save("weight", weight), 5000),
    [dayId]
  )

  const persistCalories = useMemo(
    () => debounce((calories) => save("calories", calories), 5000),
    [dayId]
  )

  useDidUpdateEffect(() => {
    persistWeight(weight).catch(() => {})
  }, [weight])

  useDidUpdateEffect(() => {
    persistCalories(calories).catch(() => {})
  }, [calories])

  useEffect(() => {
    setHistory((prev) => {
      if (!prev.length) return prev

      const copy = prev.slice()
      const i = copy.length - 1
      const cal = parseInt(calories, 10)
      const w = parseFloat(weight)
      copy[i] = {
        ...copy[i],
        calories: Number.isFinite(cal) && cal > 0 ? cal : null,
        weight: Number.isFinite(w) && w > 0 ? w : null
      }
      return copy
    })
  }, [calories, weight])

  const saveNow = (debounced, key, value) => {
    debounced.cancel()
    save(key, value)
  }

  const blurOnKey = (e) => {
    if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur()
  }

  return (
    <div className="flex flex-col gap-2 border-l-4 border-info pl-4 ">
      <a className="text-lg font-medium" href="/diet">
        Diet
      </a>

      <div>
        <div className="diet-row">
          <span className="diet-label">Weight</span>
          <div className="flex items-end">
            <input
              className="diet-input"
              type="text"
              inputMode="decimal"
              value={weight}
              onFocus={(e) => e.target.select()}
              onKeyDown={blurOnKey}
              onBlur={() => saveNow(persistWeight, "weight", weight)}
              onChange={(v) => setWeight(v.target.value.replace(",", "."))}
            />
            <div className="w-4 pb-1 text-xs text-fg-muted">kg</div>
          </div>
        </div>

        <div className="diet-row">
          <span className="diet-label">Calories</span>
          <div className="flex items-end">
            <input
              className="diet-input"
              type="text"
              inputMode="numeric"
              value={calories}
              onFocus={(e) => e.target.select()}
              onKeyDown={blurOnKey}
              onBlur={() => saveNow(persistCalories, "calories", calories)}
              onChange={(v) => setCalories(v.target.value)}
            />
            <div className="w-4 pb-1.5 text-xs text-fg-muted">ccal</div>
          </div>
        </div>
      </div>

      <CaloriesChart target={initialData.target} history={history} />
    </div>
  )
}
